import React, { useRef, useEffect } from "react";
import { FaFacebook, FaLinkedin, FaInstagram, FaXTwitter, FaPinterest } from "react-icons/fa6";

const PLATFORM_META_MAP = {
  facebook:  { icon: FaFacebook,  color: '#1877F2', label: 'Facebook'  },
  linkedin:  { icon: FaLinkedin,  color: '#0A66C2', label: 'LinkedIn'  },
  twitter:   { icon: FaXTwitter,  color: '#1DA1F2', label: 'Twitter'   },
  x:         { icon: FaXTwitter,  color: '#1DA1F2', label: 'X'         },
  instagram: { icon: FaInstagram, color: '#E4405F', label: 'Instagram' },
  pinterest: { icon: FaPinterest, color: '#E60023', label: 'Pinterest' },
};

const ChatInputBar = ({
  value,
  onChange,
  onSubmit,
  isDarkMode,
  disabled,
  isLoading,
  placeholder,
  showAttachment = false,
  fileInputRef,
  onFileSelect,
  isUploading,
  showCharacterCount = true,
  minHeight = "56px",
  currentPlan,
  availableConnectors = [],
  selectedPlatforms = [],
  togglePostPlatform,
  postMediaType,
  setPostMediaType,
  onStop,
  canStop,
  businessLogo,
  onLogoUpload,
  onLogoRemove,
  hasConversation = false,
  brands = [],
  selectedBrandId,
  onSelectBrand,
}) => {
  const textareaRef = useRef(null);
  const logoInputRef = useRef(null);

  // Auto-resize textarea as the user types
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const newHeight = Math.min(textarea.scrollHeight, 200); // cap at ~200px
    textarea.style.height = `${newHeight}px`;
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  const showSocialSelector = currentPlan?.includes('CMO') && availableConnectors.length > 0 && hasConversation;
  const canSubmit = value.trim() && !isLoading && !disabled;

  return (
    <form onSubmit={onSubmit}>
      {/* Social Media Selector (CMO only) */}
      {showSocialSelector && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {availableConnectors.map((connector) => {
              const platformKey = (connector.platform || '').toLowerCase();
              const meta = PLATFORM_META_MAP[platformKey] || {
                icon: FaInstagram, color: '#6366f1', label: connector.name || connector.platform,
              };
              const Icon = meta.icon;
              const isSelected = selectedPlatforms.includes(connector.id);
              return (
                <div key={connector.id} className="relative group/social">
                  <button
                    type="button"
                    onClick={() => togglePostPlatform(connector.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all duration-200 ${
                      isSelected
                        ? 'border-transparent text-white shadow-sm scale-105'
                        : `border-gray-200 dark:border-gray-700 ${isDarkMode ? 'text-gray-400 bg-gray-900/50' : 'text-gray-600 bg-white/50'} hover:border-gray-300 dark:hover:border-gray-600`
                    }`}
                    style={{ backgroundColor: isSelected ? meta.color : undefined }}
                  >
                    <Icon className={`text-sm ${!isSelected && 'opacity-70'}`} />
                    <span className="font-medium text-xs">{connector.name || meta.label}</span>
                    {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white ml-1 animate-pulse" />}
                  </button>
                  <div className={`absolute left-1/2 bottom-full -translate-x-1/2 mb-2 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover/social:opacity-100 transition-opacity pointer-events-none z-10 ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-900 text-white'}`}>
                    Generate a post
                  </div>
                </div>
              );
            })}
          </div>
          {/* Image / Video radio — only when ≥1 platform selected */}
          {selectedPlatforms.length > 0 && (
            <div className="flex items-center gap-4 mt-2 ml-1 flex-wrap">
              <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Media type:</span>
              {['image', 'video'].map(type => (
                <label key={type} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="postMediaType"
                    value={type}
                    checked={postMediaType === type}
                    onChange={() => setPostMediaType(type)}
                    className="accent-cyan-500"
                  />
                  <span className={`text-xs capitalize ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{type}</span>
                </label>
              ))}

              {/* Logo upload */}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.[0]) onLogoUpload(e.target.files[0]);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-all ${
                  businessLogo
                    ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10'
                    : isDarkMode
                      ? 'border-gray-600 text-gray-400 hover:border-gray-400 hover:text-gray-200'
                      : 'border-gray-300 text-gray-500 hover:border-gray-500 hover:text-gray-700'
                }`}
                title={businessLogo ? 'Logo uploaded — click to replace' : 'Upload business logo'}
              >
                {businessLogo ? (
                  <img src={businessLogo} alt="logo" className="w-4 h-4 rounded-full object-cover" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
                {businessLogo ? 'Logo saved' : 'Add logo'}
              </button>
              {businessLogo && (
                <button
                  type="button"
                  onClick={onLogoRemove}
                  className={`w-5 h-5 flex items-center justify-center rounded-full border text-xs transition-all border-red-400 text-red-400 hover:bg-red-400/10`}
                  title="Remove logo"
                >
                  ×
                </button>
              )}

              {/* Brand selector */}
              {brands.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <svg className={`w-3.5 h-3.5 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <select
                    value={selectedBrandId || ""}
                    onChange={(e) => onSelectBrand(e.target.value || null)}
                    className={`text-xs rounded-lg border px-2 py-1 outline-none focus:ring-1 focus:ring-cyan-500 transition-all cursor-pointer ${
                      selectedBrandId
                        ? isDarkMode ? 'bg-cyan-900/40 border-cyan-600 text-cyan-300' : 'bg-cyan-50 border-cyan-400 text-cyan-700'
                        : isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-600'
                    }`}
                  >
                    <option value="">No brand</option>
                    {brands.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div
        className={`relative ${isDarkMode
          ? "bg-gray-900 border-gray-800"
          : "bg-white border-gray-200"
          } border rounded-xl overflow-hidden ${isDarkMode
            ? "hover:border-gray-700"
            : "hover:border-gray-300"
          } transition-colors`}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          rows={1}
          className={`w-full px-6 py-4 bg-transparent ${isDarkMode
            ? "text-white placeholder-gray-500"
            : "text-gray-900 placeholder-gray-400"
            } focus:outline-none resize-none overflow-y-auto ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
          style={{ minHeight: minHeight }}
        />

        {/* Hidden File Input */}
        {showAttachment && (
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileSelect}
            className="hidden"
            accept="image/*, application/pdf, .doc, .docx"
          />
        )}

        <div
          className={`flex items-center justify-between px-4 py-2 border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"
            }`}
        >
          <div className="flex items-center gap-2">
            {/* Attachment Button */}
            {showAttachment && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isLoading}
                className={`p-2 rounded-lg transition-all ${isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-800"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                  } ${isUploading ? "animate-pulse cursor-wait" : ""}`}
                title="Upload file"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
            )}

            {showCharacterCount && (
              <span
                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
              >
                {value.length} characters
              </span>
            )}
          </div>

          {isLoading && onStop ? (
            <button
              type="button"
              onClick={onStop}
              disabled={!canStop}
              className={`p-2.5 rounded-lg transition-all ${
                canStop
                  ? isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : isDarkMode ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              title={canStop ? 'Stop generating' : 'Waiting...'}
            >
              {canStop ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" rx="1.5" />
                </svg>
              ) : (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              )}
            </button>
          ) : (
          <button
            type="submit"
            disabled={!canSubmit}
            className={`p-2.5 rounded-lg transition-all ${canSubmit
              ? "bg-cyan-500 text-white hover:bg-cyan-600"
              : isDarkMode
                ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                ></path>
              </svg>
            )}
          </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default ChatInputBar;
